import {
    Add as AddIcon,
    DeleteOutline as DeleteIcon,
    Remove as RemoveIcon,
    WarningAmber,
} from "@mui/icons-material";
import {
    Box,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCartItemKey } from "../../context/cartContext";

const getNormalizedProductId = (item) =>
    item?.ProductID ?? item?.product_id ?? item?.productId ?? item?.id;
const toProductIdKey = (value) => String(value ?? "");
const normalizeScopeValue = (value) => {
    const normalized = String(value ?? "").trim();
    return normalized && normalized !== "undefined" && normalized !== "null" ? normalized : "";
};
const getScopeKey = (item = {}) => {
    const siteId = normalizeScopeValue(item?.site_id ?? item?.siteId);
    const cusId = normalizeScopeValue(item?.cus_id ?? item?.customer_id ?? item?.customerId);
    return siteId && cusId ? `${siteId}:${cusId}` : "";
};

const buildBomRefs = (item) => {
    const bomRefsRaw = Array.isArray(item?.bom_refs) ? item.bom_refs : [];
    return Array.from(
        bomRefsRaw.reduce((acc, ref) => {
            const level = Number.isFinite(Number(ref?.bom_level)) ? Number(ref.bom_level) : "-";
            const qty = Number(ref?.bom_qty) || 1;
            const isTopLevelBom = Number(level) === 1;
            const parentAssemblyRaw =
                ref?.assembly_name ||
                ref?.parent_assembly ||
                ref?.parent_name ||
                ref?.parent_product_name ||
                ref?.parent_product_number ||
                "Top-level assembly";
            const normalizedParentAssembly =
                !parentAssemblyRaw ||
                String(parentAssemblyRaw).trim() === "" ||
                /^N\/A\b\.?$/i.test(String(parentAssemblyRaw).trim()) ||
                /^Top-level assembly$/i.test(String(parentAssemblyRaw).trim())
                    ? ""
                    : String(parentAssemblyRaw).trim();
            const parentAssembly = isTopLevelBom ? "" : normalizedParentAssembly;
            const bomPath = ref?.bom_path || "";
            const key = `${level}|${parentAssembly}|${bomPath}`;
            const existing = acc.get(key);
            if (!existing) {
                acc.set(key, {
                    bom_level: level,
                    bom_qty: qty,
                    parent_assembly: parentAssembly,
                    bom_path: bomPath,
                });
            } else {
                existing.bom_qty += qty;
            }
            return acc;
        }, new Map()).values()
    );
};

const BASE_ROW_HEIGHT = 78;
const PER_EXTRA_REF_HEIGHT = 58;
const OVERSCAN = 6;
const DEFAULT_VIEWPORT = 600;

const estimateRowHeight = (refCount) =>
    refCount > 1 ? BASE_ROW_HEIGHT + (refCount - 1) * PER_EXTRA_REF_HEIGHT : BASE_ROW_HEIGHT;

const CartRow = React.memo(function CartRow({
    item,
    bomRefs,
    currentQuantity,
    quantityInputValue,
    cartKey,
    stockImageSrc,
    maxQtyAllowed,
    handleInputChange,
    handleInputBlur,
    handleKeyPress,
    handleKeyDown,
    handleQuantityChange,
    handleRemoveItem,
    handleNavigateToProduct,
    t,
}) {
    const productData = item?.product_data || null;
    if (!productData) return null;

    const machineNumber =
        productData?.machine_number ||
        productData?.machineNumber ||
        item?.machine_number ||
        item?.machineData?.machine_number ||
        "N/A";
    const lineName =
        productData?.line_name ||
        productData?.lineName ||
        item?.line_name ||
        item?.lineName ||
        item?.machineData?.line_name ||
        "";
    const lineLabel = t("TABLE_HEADERS.LINE", { defaultValue: "Line" });
    const metadataTitle = [
        productData?.product_number,
        machineNumber ? `${t("UI_TEXT.MACHINE")} ${machineNumber}` : null,
        lineName ? `${lineLabel}: ${lineName}` : null,
    ]
        .filter(Boolean)
        .join(" | ");
    const parentLabelRaw = t("UI_TEXT.PARENT", { defaultValue: "Parent" });
    const parentLabel = String(parentLabelRaw || "Parent").replace(/:\s*$/, "");
    const bomLevel = Number.isFinite(Number(item?.bom_level)) ? Number(item.bom_level) : null;

    return (
        <TableRow
            sx={{
                ml: 1,
                "& td, & th": {
                    borderBottom: "1px solid rgba(224,224,224,1)",
                },
                "& td:first-of-type": {
                    borderBottom: "none",
                },
                "&:last-child td, &:last-child th": {
                    border: 0,
                },
            }}
        >
            <TableCell width={30} />
            <TableCell>
                <Box className="product-cell" sx={{ display: "flex", alignItems: "center" }}>
                    <img
                        src={item.imageSrc}
                        alt={productData.product_name}
                        onError={(e) => {
                            if (stockImageSrc && e?.currentTarget?.src !== stockImageSrc) {
                                e.currentTarget.src = stockImageSrc;
                            }
                        }}
                        className="product-image"
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                        width={40}
                        height={40}
                        style={{ width: 40, height: 40, marginRight: 8 }}
                    />
                    <Box className="product-info" sx={{ overflow: "hidden", width: "100%" }}>
                        {bomRefs.length > 0 ? (
                            bomRefs.map((ref, idx) => (
                                <Box
                                    key={`${productData.product_id}-bom-ref-${idx}`}
                                    sx={{
                                        mt: idx === 0 ? 0 : 1,
                                        pt: idx === 0 ? 0 : 1,
                                        borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.08)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Tooltip title={productData.product_name}>
                                            <Link
                                                underline="hover"
                                                sx={{
                                                    cursor: "pointer",
                                                    maxWidth: 350,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "inline-block",
                                                }}
                                                onClick={() => {
                                                    const url = `/machines/${productData.machine_id}/${productData.product_id}`;
                                                    handleNavigateToProduct(url);
                                                }}
                                            >
                                                {productData.product_name}
                                            </Link>
                                        </Tooltip>
                                        {item.showDisclaimer && idx === 0 && (
                                            <WarningAmber color="warning" fontSize="small" />
                                        )}
                                    </Box>
                                    <Tooltip title={productData.product_number}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                maxWidth: 350,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "block",
                                                fontSize: "11px",
                                            }}
                                        >
                                            <b>{t("UI_TEXT.PART")}</b> {productData.product_number} |{" "}
                                            <b>{t("UI_TEXT.MACHINE")}</b> {machineNumber} |{" "}
                                            <b>{lineLabel}:</b> {lineName}
                                        </Typography>
                                    </Tooltip>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: "11px", fontWeight: 400, lineHeight: 1.35 }}
                                    >
                                        <span style={{ fontWeight: 600 }}>BOM Level</span>: {ref.bom_level}
                                        {"  "}
                                        <span style={{ color: "#9aa0a6" }}>|</span>
                                        {"  "}
                                        <span style={{ fontWeight: 600 }}>Quantity in BOM chain</span>: {ref.bom_qty}
                                        {ref.parent_assembly ? (
                                            <>
                                                {"  "}
                                                <span style={{ color: "#9aa0a6" }}>|</span>
                                                {"  "}
                                                <span style={{ fontWeight: 600 }}>{parentLabel}</span>:{" "}
                                                {ref.parent_assembly}
                                            </>
                                        ) : null}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Tooltip title={productData.product_name}>
                                        <Link
                                            underline="hover"
                                            sx={{
                                                cursor: "pointer",
                                                maxWidth: 350,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "inline-block",
                                            }}
                                            onClick={() => {
                                                const url = `/machines/${productData.machine_id}/${productData.product_id}`;
                                                handleNavigateToProduct(url);
                                            }}
                                        >
                                            {productData.product_name}
                                        </Link>
                                    </Tooltip>
                                    {item.showDisclaimer && (
                                        <WarningAmber color="warning" fontSize="small" />
                                    )}
                                </Box>

                                <Tooltip title={metadataTitle}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            maxWidth: 350,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            display: "block",
                                            fontSize: "11px",
                                        }}
                                    >
                                        <b>{t("UI_TEXT.PART")}</b> {productData.product_number} |{" "}
                                        <b>{t("UI_TEXT.MACHINE")}</b> {machineNumber} |{" "}
                                        <b>{lineLabel}:</b> {lineName}
                                    </Typography>
                                </Tooltip>
                                {bomLevel !== null && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: "11px" }}
                                    >
                                        <b>BOM Level</b>: {bomLevel}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </TableCell>

            <TableCell align="center">
                <Box className="quantity-controls">
                    <IconButton
                        size="small"
                        disabled={currentQuantity <= 1}
                        onClick={() => handleQuantityChange(productData.product_id, -1, cartKey)}
                    >
                        <RemoveIcon />
                    </IconButton>
                    <TextField
                        type="text"
                        size="small"
                        value={
                            quantityInputValue !== undefined
                                ? quantityInputValue
                                : currentQuantity.toString()
                        }
                        onChange={(e) => handleInputChange(productData.product_id, e.target.value)}
                        onBlur={() => handleInputBlur(productData.product_id, cartKey)}
                        onKeyPress={(e) => handleKeyPress(productData.product_id, e, cartKey)}
                        onKeyDown={(e) => handleKeyDown(productData.product_id, e, cartKey)}
                        className="quantity-input"
                        placeholder="Qty"
                        inputProps={{
                            style: { textAlign: "center" },
                            "aria-label": t("ARIA_LABELS.QUANTITY"),
                            inputMode: "numeric",
                            maxLength: 6,
                            max: maxQtyAllowed,
                        }}
                    />
                    <IconButton
                        size="small"
                        disabled={currentQuantity >= maxQtyAllowed}
                        onClick={() => handleQuantityChange(productData.product_id, 1, cartKey)}
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
            </TableCell>

            <TableCell>
                <Tooltip title={t("UI_TEXT.REMOVE_PART_FROM_CART")} arrow placement="right">
                    <IconButton
                        color="primary"
                        onClick={() => handleRemoveItem(productData.product_id, cartKey)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
});

const CartTable = ({
    groupedCartProducts,
    cartItems,
    stockImageSrc,
    maxQtyAllowed,
    quantityInputs,
    handleInputChange,
    handleInputBlur,
    handleKeyPress,
    handleKeyDown,
    handleQuantityChange,
    handleRemoveItem,
    handleNavigateToProduct,
    scrollContainerRef,
    t,
}) => {
    const cartItemsByKey = useMemo(() => {
        const map = new Map();
        (cartItems || []).forEach((ci) => {
            if (!ci) return;
            const key = getCartItemKey(ci);
            if (!map.has(key)) map.set(key, ci);
        });
        return map;
    }, [cartItems]);

    const rows = useMemo(() => {
        const flat = [];
        Object.keys(groupedCartProducts || {}).forEach((ctpDate) => {
            (groupedCartProducts[ctpDate] || []).forEach((item) => {
                if (!item || !item.product_data) return;
                const bomRefs = buildBomRefs(item);
                flat.push({
                    item,
                    bomRefs,
                    cartKey: getCartItemKey(item),
                    height: estimateRowHeight(bomRefs.length),
                });
            });
        });
        return flat;
    }, [groupedCartProducts]);

    const offsets = useMemo(() => {
        const acc = new Array(rows.length + 1);
        acc[0] = 0;
        for (let i = 0; i < rows.length; i += 1) acc[i + 1] = acc[i] + rows[i].height;
        return acc;
    }, [rows]);

    const totalHeight = offsets[rows.length] || 0;

    const [viewport, setViewport] = useState({ scrollTop: 0, height: DEFAULT_VIEWPORT });
    const [isMeasured, setIsMeasured] = useState(false);
    const frameRef = useRef(null);

    useEffect(() => {
        const el = scrollContainerRef?.current;
        if (!el) return undefined;

        const read = () => {
            frameRef.current = null;
            setIsMeasured(true);
            setViewport((prev) => {
                const next = { scrollTop: el.scrollTop, height: el.clientHeight || DEFAULT_VIEWPORT };
                if (prev.scrollTop === next.scrollTop && prev.height === next.height) return prev;
                return next;
            });
        };
        const onScroll = () => {
            if (frameRef.current !== null) return;
            frameRef.current = window.requestAnimationFrame(read);
        };

        read();
        el.addEventListener("scroll", onScroll, { passive: true });
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(read) : null;
        if (observer) observer.observe(el);

        return () => {
            el.removeEventListener("scroll", onScroll);
            if (observer) observer.disconnect();
            if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
        };
    }, [scrollContainerRef, totalHeight]);

    const { startIndex, endIndex } = useMemo(() => {
        if (rows.length === 0) return { startIndex: 0, endIndex: 0 };
        if (!isMeasured || !scrollContainerRef?.current) {
            return { startIndex: 0, endIndex: Math.min(rows.length, 30) };
        }
        const top = viewport.scrollTop;
        const bottom = top + viewport.height;
        let start = 0;
        let end = rows.length;
        let lo = 0;
        let hi = rows.length;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (offsets[mid + 1] <= top) lo = mid + 1;
            else hi = mid;
        }
        start = lo;
        let i = start;
        while (i < rows.length && offsets[i] < bottom) i += 1;
        end = i;
        return {
            startIndex: Math.max(0, start - OVERSCAN),
            endIndex: Math.min(rows.length, end + OVERSCAN),
        };
    }, [rows.length, offsets, viewport, isMeasured, scrollContainerRef]);

    const topSpacer = offsets[startIndex] || 0;
    const bottomSpacer = Math.max(0, totalHeight - (offsets[endIndex] || 0));

    const visible = rows.slice(startIndex, endIndex);

    const getQuantity = useCallback(
        (item, cartKey) => {
            const cartItem = cartItemsByKey.get(cartKey);
            return cartItem ? cartItem.Quantity : item.Quantity;
        },
        [cartItemsByKey]
    );

    return (
        <TableContainer sx={{ borderRadius: "10px", overflow: "hidden" }}>
            <Table>
                <TableHead sx={{ mt: 1 }}>
                    <TableRow>
                        <TableCell width={30} />
                        <TableCell>{t("TABLE_HEADERS.PARTS_QUANTITY")}</TableCell>
                        <TableCell align="center">{t("TABLE_HEADERS.QUANTITY")}</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {topSpacer > 0 && (
                        <TableRow style={{ height: topSpacer }}>
                            <TableCell colSpan={4} style={{ padding: 0, border: 0 }} />
                        </TableRow>
                    )}
                    {visible.map(({ item, bomRefs, cartKey }) => {
                        const productId = item.product_data.product_id;
                        return (
                            <CartRow
                                key={`${productId}-${cartKey}`}
                                item={item}
                                bomRefs={bomRefs}
                                cartKey={cartKey}
                                currentQuantity={getQuantity(item, cartKey)}
                                quantityInputValue={quantityInputs[productId]}
                                stockImageSrc={stockImageSrc}
                                maxQtyAllowed={maxQtyAllowed}
                                handleInputChange={handleInputChange}
                                handleInputBlur={handleInputBlur}
                                handleKeyPress={handleKeyPress}
                                handleKeyDown={handleKeyDown}
                                handleQuantityChange={handleQuantityChange}
                                handleRemoveItem={handleRemoveItem}
                                handleNavigateToProduct={handleNavigateToProduct}
                                t={t}
                            />
                        );
                    })}
                    {bottomSpacer > 0 && (
                        <TableRow style={{ height: bottomSpacer }}>
                            <TableCell colSpan={4} style={{ padding: 0, border: 0 }} />
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CartTable;
